import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  Button,
  Column,
  DataTable,
  Form,
  Row,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TextArea,
  TextInput,
  Tag,
} from "carbon-components-react";
import { TrashCan24, Upload16, Search32 } from "@carbon/icons-react";
import { useForm } from "react-hook-form";
import Card from "../../components/Card/Card";
import TablePagination from "../../components/Pagination/Pagination";
import http from "../../http-common";
import { toast } from "react-toastify";

const previewHeaders = [
  { header: "ID", key: "id" },
  { header: "Nome carregado", key: "uploadedName" },
  { header: "Nome no sistema", key: "systemName" },
  { header: "Estado", key: "status" },
  { header: "Ações", key: "actions" },
];

const eligibleHeaders = [
  { header: "ID", key: "id" },
  { header: "Nome", key: "name" },
  { header: "Telefone", key: "phoneNumber" },
  { header: "Ações", key: "actions" },
];

const parseRows = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [id, ...nameParts] = line.split(",");
      return { id: Number(id.trim()), name: nameParts.join(",").trim() };
    })
    .filter((row) => !Number.isNaN(row.id));

const WindowEligibilityPage = () => {
  const [pastedText, setPastedText] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Eligible list is paginated and searched server-side, same pattern as
  // UsersPage: only the current page is fetched, not the whole table.
  const [eligibleList, setEligibleList] = useState([]);
  const [count, setCount] = useState(0);
  const [size, setSize] = useState(20);
  const { register, handleSubmit } = useForm();

  const getEligibleData = (response) => {
    setEligibleList(response.data.content);
    setCount(response.data.totalElements);
  };

  const fetchEligibleList = async () => {
    try {
      const response = await http.get("/admin/window2-eligibility?size=" + size);
      getEligibleData(response);
    } catch (error) {
      toast.error("Erro ao carregar a lista de candidatos elegíveis");
    }
  };

  const onPageChange = async ({ page, pageSize }) => {
    try {
      setSize(pageSize);
      const response = await http.get(
        `/admin/window2-eligibility?page=${page - 1}&size=${pageSize}`
      );
      getEligibleData(response);
    } catch (error) {
      toast.error("Erro ao carregar a lista de candidatos elegíveis");
    }
  };

  const onSearch = async (data) => {
    try {
      const response = await http.get(
        `/admin/window2-eligibility?size=${size}&name=${data.name || ""}&phoneNumber=${
          data.phoneNumber || ""
        }`
      );
      getEligibleData(response);
    } catch (error) {
      toast.error("Erro ao pesquisar candidatos elegíveis");
    }
  };

  useEffect(() => {
    fetchEligibleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const lines = rows
        .filter((row) => row.length > 0 && row[0] !== undefined && row[0] !== "")
        .map((row) => `${row[0]},${row[1] || ""}`);
      setPastedText(lines.join("\n"));
    };
    reader.readAsBinaryString(file);
    event.target.value = "";
  };

  const handlePreview = async () => {
    const rows = parseRows(pastedText);
    if (rows.length === 0) {
      toast.error("Cole ou carregue pelo menos um candidato (id,nome)");
      return;
    }

    try {
      setIsPreviewing(true);
      const response = await http.post("/admin/window2-eligibility/preview", rows);
      setPreviewRows(response.data);
      setExcludedIds([]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao rever a lista carregada");
    } finally {
      setIsPreviewing(false);
    }
  };

  const excludeRow = (id) => {
    setExcludedIds((prev) => [...prev, id]);
  };

  const handleConfirm = async () => {
    const ids = previewRows
      .filter((row) => row.found && !excludedIds.includes(row.id))
      .map((row) => row.id);

    if (ids.length === 0) {
      toast.error("Não há candidatos válidos para confirmar");
      return;
    }

    try {
      setIsConfirming(true);
      const response = await http.post("/admin/window2-eligibility/confirm", ids);
      const { successCount, alreadyEligibleCount, notFoundCount } = response.data;
      toast.success(
        `${successCount} candidato(s) habilitado(s) para a Janela 2. ` +
          `${alreadyEligibleCount} já estavam habilitados. ${notFoundCount} não encontrados.`
      );
      setPreviewRows([]);
      setPastedText("");
      setExcludedIds([]);
      fetchEligibleList();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao confirmar a habilitação");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await http.delete(`/admin/window2-eligibility/${id}`);
      toast.success("Habilitação para a Janela 2 revogada");
      fetchEligibleList();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao revogar a habilitação");
    }
  };

  const visibleRows = previewRows
    .filter((row) => !excludedIds.includes(row.id))
    .map((row) => ({
      id: String(row.id),
      uploadedName: row.uploadedName,
      systemName: row.systemName || "-",
      status: !row.found ? (
        <Tag type="red">Não encontrado</Tag>
      ) : row.alreadyEligible ? (
        <Tag type="cyan">Já elegível</Tag>
      ) : (
        <Tag type="green">Elegível</Tag>
      ),
      actions: (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          iconDescription="Excluir da lista"
          renderIcon={TrashCan24}
          onClick={() => excludeRow(row.id)}
        />
      ),
    }));

  const eligibleRows = eligibleList.map((candidate) => ({
    id: String(candidate.id),
    name: candidate.name,
    phoneNumber: candidate.phoneNumber,
    actions: (
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        iconDescription="Revogar"
        renderIcon={TrashCan24}
        className="emprega-orange"
        onClick={() => handleRevoke(candidate.id)}
      />
    ),
  }));

  return (
    <Card title="Elegibilidade para a Janela 2">
      <p className="mb-2">
        Carregue ou cole a lista de candidatos (ID e Nome, um por linha, separados por
        vírgula) que devem ser habilitados a submeter uma segunda candidatura e plano de
        negócio na Janela 2. Rever a lista antes de confirmar permite excluir candidatos
        indesejados.
      </p>

      <TextArea
        id="pasted-candidates"
        labelText="Lista de candidatos (id,nome por linha)"
        placeholder={"12345,João Manuel\n67890,Maria Fernanda"}
        rows={6}
        value={pastedText}
        onChange={(e) => setPastedText(e.target.value)}
      />

      <div className="mb-2 form-buttons" style={{ marginTop: "1rem" }}>
        <label className="bx--btn bx--btn--tertiary" htmlFor="file-upload">
          <Upload16 style={{ marginRight: "0.5rem" }} />
          Carregar ficheiro (.xlsx/.csv)
        </label>
        <input
          id="file-upload"
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <Button renderIcon={Search32} disabled={isPreviewing} onClick={handlePreview}>
          Rever Lista
        </Button>
      </div>

      {previewRows.length > 0 && (
        <>
          <DataTable rows={visibleRows} headers={previewHeaders} useZebraStyles>
            {({ rows, headers, getHeaderProps, getTableProps }) => (
              <TableContainer title="Pré-visualização">
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableHeader {...getHeaderProps({ header })}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>

          <div className="mb-2 form-buttons" style={{ marginTop: "1rem" }}>
            <Button
              className="emprega-bg-green"
              disabled={isConfirming}
              onClick={handleConfirm}
            >
              Confirmar Habilitação para Janela 2
            </Button>
          </div>
        </>
      )}

      <h4 style={{ marginTop: "3rem" }}>Candidatos já habilitados para a Janela 2</h4>
      <DataTable rows={eligibleRows} headers={eligibleHeaders} useZebraStyles>
        {({ rows, headers, getHeaderProps, getTableProps }) => (
          <TableContainer>
            <TableToolbar aria-label="data table toolbar">
              <TableToolbarContent>
                <Form className="data-table-search-form" onSubmit={handleSubmit(onSearch)}>
                  <Row>
                    <Column sm={6} md={4} lg={4}>
                      <TextInput
                        id="eligible-name"
                        labelText=""
                        placeholder="Nome"
                        {...register("name")}
                      />
                    </Column>
                    <Column sm={6} md={4} lg={4}>
                      <TextInput
                        id="eligible-phoneNumber"
                        labelText=""
                        placeholder="Telefone"
                        {...register("phoneNumber")}
                      />
                    </Column>
                  </Row>
                  <Button type="submit" renderIcon={Search32}>
                    Pesquisar
                  </Button>
                </Form>
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
      <TablePagination currentPageSize={size} onPageChange={onPageChange} totalItems={count} />
    </Card>
  );
};

export default WindowEligibilityPage;
